import type { i18n as I18nInstance } from 'i18next';
import i18n from './i18n';
import { namespaceForPage, stripPageSeoMetadata } from './locale-merge';
import { resolveLocale, translationSpecForComponent } from './i18n-page-map';

type JsonObject = Record<string, unknown>;
type JsonModule = { default?: JsonObject } | JsonObject;

const publicPageLoaders = import.meta.glob<JsonModule>('../locales/*/public/pages/*.json');
const publicCommonLoaders = import.meta.glob<JsonModule>('../locales/*/public/common.json');

const appLoaders = {
    de: () => import('../locales/de/de-app.json'),
    en: () => import('../locales/en/en-app.json'),
} as const;

const helpLoaders = {
    de: () => import('../locales/de/de-help.json'),
    en: () => import('../locales/en/en-help.json'),
} as const;

const activityLoaders = {
    de: () => import('../locales/de/de-activity.json'),
    en: () => import('../locales/en/en-activity.json'),
} as const;

const helpdeskLoaders = {
    de: () => import('../locales/de/de-helpdesk.json'),
    en: () => import('../locales/en/en-helpdesk.json'),
} as const;

const importCache = new Map<string, Promise<JsonObject>>();

function unwrap(module: JsonModule): JsonObject {
    const candidate = (module as { default?: unknown }).default;

    if (candidate !== null && typeof candidate === 'object' && !Array.isArray(candidate)) {
        return candidate as JsonObject;
    }

    return module as JsonObject;
}

export function resetTranslationImportCache(): void {
    importCache.clear();
}

export function importTranslationOnce(key: string, loader: () => Promise<JsonModule>): Promise<JsonObject> {
    const cached = importCache.get(key);

    if (cached) {
        return cached;
    }

    const pending = loader()
        .then(unwrap)
        .catch((error: unknown) => {
            importCache.delete(key);
            throw error;
        });

    importCache.set(key, pending);

    return pending;
}

async function addBundle(instance: I18nInstance, locale: 'de' | 'en', namespace: string, data: JsonObject): Promise<void> {
    instance.addResourceBundle(locale, namespace, data, true, true);
}

async function loadCommon(locale: 'de' | 'en', instance: I18nInstance): Promise<void> {
    const path = `../locales/${locale}/public/common.json`;
    const loader = publicCommonLoaders[path];

    if (!loader) {
        throw new Error(`Missing public common translations: ${path}`);
    }

    const data = await importTranslationOnce(`common:${locale}`, loader);

    if (data.translation && typeof data.translation === 'object') {
        await addBundle(instance, locale, 'translation', data.translation as JsonObject);
    }

    if (data['marketing-pages'] && typeof data['marketing-pages'] === 'object') {
        await addBundle(instance, locale, 'marketing-pages', data['marketing-pages'] as JsonObject);
    }
}

async function loadPublicPage(locale: 'de' | 'en', pageName: string, instance: I18nInstance): Promise<void> {
    const path = `../locales/${locale}/public/pages/${pageName}.json`;
    const loader = publicPageLoaders[path];

    if (!loader) {
        throw new Error(`Missing public page translations: ${path}`);
    }

    const data = await importTranslationOnce(`page:${locale}:${pageName}`, loader);

    await addBundle(instance, locale, namespaceForPage(pageName), {
        [pageName]: stripPageSeoMetadata(data),
    });
}

export async function loadPageTranslations(
    locale: string | undefined,
    component: string,
    instance: I18nInstance = i18n,
): Promise<void> {
    const resolved = resolveLocale(locale);
    const spec = translationSpecForComponent(component);
    const tasks: Promise<void>[] = [];

    if (spec.loadCommon) {
        tasks.push(loadCommon(resolved, instance));
    }

    for (const pageName of spec.publicPages) {
        tasks.push(loadPublicPage(resolved, pageName, instance));
    }

    if (spec.loadApp) {
        tasks.push(
            importTranslationOnce(`app:${resolved}`, appLoaders[resolved]).then((data) => addBundle(instance, resolved, 'translation', data)),
        );
        tasks.push(
            importTranslationOnce(`help:${resolved}`, helpLoaders[resolved]).then((data) => addBundle(instance, resolved, 'help', data)),
        );
        tasks.push(
            importTranslationOnce(`activity:${resolved}`, activityLoaders[resolved]).then((data) =>
                addBundle(instance, resolved, 'activity', data),
            ),
        );
    }

    if (spec.loadHelpdesk) {
        tasks.push(
            importTranslationOnce(`helpdesk:${resolved}`, helpdeskLoaders[resolved]).then((data) =>
                addBundle(instance, resolved, 'helpdesk', data),
            ),
        );
    }

    await Promise.all(tasks);

    if (instance.language !== resolved) {
        await instance.changeLanguage(resolved);
    }
}
